import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import type { ToastApi, ToastItem, ToastVariant } from './Toast.types';
import './Toast.scss';

const MAX = 5;
const DEFAULT_DURATION = 4000;
const ToastCtx = createContext<ToastApi | null>(null);

const iconFor = (v: ToastVariant) => {
  if (v === 'success') return <CheckCircle2 size={18} />;
  if (v === 'error') return <AlertCircle size={18} />;
  if (v === 'warning') return <AlertTriangle size={18} />;
  return <Info size={18} />;
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setItems(arr => arr.filter(t => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) { window.clearTimeout(tm); timers.current.delete(id); }
  }, []);

  const show = useCallback((item: Omit<ToastItem, 'id'>): string => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const full: ToastItem = { id, duration: DEFAULT_DURATION, ...item };
    setItems(arr => [...arr, full].slice(-MAX));
    const tm = window.setTimeout(() => dismiss(id), full.duration);
    timers.current.set(id, tm);
    return id;
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (message, title) => show({ variant: 'success', message, title }),
    error: (message, title) => show({ variant: 'error', message, title }),
    info: (message, title) => show({ variant: 'info', message, title }),
    warning: (message, title) => show({ variant: 'warning', message, title }),
    dismiss,
  }), [show, dismiss]);

  useEffect(() => () => { timers.current.forEach(t => window.clearTimeout(t)); }, []);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toast-stack" role="region" aria-live="polite" aria-label="Уведомления">
        {items.map(t => <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />)}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const startX = useRef<number | null>(null);
  const deltaX = useRef(0);
  const [tx, setTx] = useState(0);

  const onTouchStart = (e: React.TouchEvent) => { startX.current = e.touches[0].clientX; };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    deltaX.current = e.touches[0].clientX - startX.current;
    setTx(deltaX.current);
  };
  const onTouchEnd = () => {
    if (Math.abs(deltaX.current) > 80) onDismiss();
    else setTx(0);
    startX.current = null; deltaX.current = 0;
  };

  return (
    <div
      role="status"
      className={['toast', `toast--${item.variant}`].join(' ')}
      style={{ transform: `translateX(${tx}px)`, opacity: tx ? Math.max(0.3, 1 - Math.abs(tx) / 200) : 1 }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <span className="toast__icon" aria-hidden="true">{iconFor(item.variant)}</span>
      <div className="toast__content">
        {item.title ? <div className="toast__title">{item.title}</div> : null}
        <div className="toast__message">{item.message}</div>
      </div>
      <button type="button" aria-label="Закрыть" className="toast__close" onClick={onDismiss}><X size={14} /></button>
    </div>
  );
}

export default ToastProvider;
